import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Check if a URL is a presigned S3 URL (which can expire)
 */
export function isPresignedUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return url.includes('AWSAccessKeyId=') ||
         url.includes('X-Amz-Signature=') ||
         url.includes('Expires=');
}

/**
 * Resolve a video URL to a non-expiring proxy URL when possible.
 * Presigned S3 URLs expire after ~7 days, but proxy URLs never expire.
 *
 * @param s3Url - The stored S3 URL (may be presigned or proxy)
 * @param s3Key - The S3 object key (used to construct proxy URL)
 * @returns A working video URL, preferring proxy URLs for reliability
 */
export function resolveVideoUrl(s3Url?: string | null, s3Key?: string | null): string | null {
  // If we have an s3Key and the URL is presigned (or missing), use proxy URL
  if (s3Key && (!s3Url || isPresignedUrl(s3Url))) {
    return `/api/s3/${s3Key}`;
  }

  // Otherwise return the stored URL
  return s3Url || null;
}

/**
 * Resolve an aspect ratio selection into Shotstack output parameters.
 * Handles "source" (match source video dimensions) and standard presets.
 */
export function resolveOutputConfig(
  aspectRatio: string,
  videoFiles: Array<{ width?: number; height?: number }>
) {
  const presets: Record<string, { resolution: string; width: number; height: number }> = {
    '16:9': { resolution: 'hd', width: 1920, height: 1080 },
    '9:16': { resolution: 'mobile', width: 1080, height: 1920 },
    '1:1':  { resolution: 'custom', width: 1080, height: 1080 },
    '4:5':  { resolution: 'custom', width: 1080, height: 1350 },
  };

  if (aspectRatio !== 'source') {
    const preset = presets[aspectRatio] || presets['16:9'];
    return {
      shotstack_resolution: preset.resolution === 'hd' || preset.resolution === 'mobile' ? preset.resolution : undefined,
      output_width: preset.width,
      output_height: preset.height,
    };
  }

  // "Match Source" — use dimensions from first video with width/height
  const sourceFile = videoFiles.find(f => f.width && f.height);
  if (sourceFile && sourceFile.width && sourceFile.height) {
    let w = sourceFile.width;
    let h = sourceFile.height;
    const maxDim = 1920;
    if (w > maxDim || h > maxDim) {
      if (w >= h) {
        h = Math.round((h / w) * maxDim);
        w = maxDim;
      } else {
        w = Math.round((w / h) * maxDim);
        h = maxDim;
      }
    }
    // Shotstack requires even dimensions
    w = w % 2 === 0 ? w : w + 1;
    h = h % 2 === 0 ? h : h + 1;
    return { shotstack_resolution: undefined, output_width: w, output_height: h };
  }

  // Fallback if no dimensions available
  return { shotstack_resolution: 'hd', output_width: 1920, output_height: 1080 };
}

/**
 * Format a YYYY-MM-DD booking date string for display, treating it as a
 * local-time date (NOT UTC). The reader-booking pipeline stores
 * `requested_session_date` as the user's intended local date alongside a
 * separate `timezone` field, so a string like "2026-05-06" should always
 * render as May 6 regardless of the viewer's timezone. Passing it through
 * `new Date(str)` parses as UTC midnight, which then renders as the prior
 * day in any TZ west of UTC (e.g. Pacific shows May 5).
 */
export function formatBookingDate(
  yyyy_mm_dd?: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
): string {
  if (!yyyy_mm_dd) return '';
  const m = yyyy_mm_dd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    try { return new Date(yyyy_mm_dd).toLocaleDateString('en-US', options); }
    catch { return yyyy_mm_dd; }
  }
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d)).toLocaleDateString('en-US', options);
}
